/**
 * Portal text overrides at runtime (Portal text CMS, docs/specs/portal-text-cms.md).
 * Loads the PUBLISHED staff overrides of the current locale from /bff/v1/cms/public/ui-text (anonymous; sign-in screens
 * need them too) and applies them with @qm/i18n setTextOverrides. It never blocks rendering and never throws:
 * a slow, failed or malformed answer silently leaves the shipped text in place.
 * The last good answer is kept in memory and in localStorage (keyed by locale, carrying the content version) so the next
 * visit starts with it before the network answers. Drafts never reach this code: the API serves published text only.
 */
import { isLocale, setTextOverrides, type Locale } from '@qm/i18n';

export const UI_TEXT_TIMEOUT_MS = 2500;
/** A locale is not refetched more often than this (locale switches and remounts reuse the memory copy). */
export const UI_TEXT_REFRESH_MS = 30_000;
export const uiTextStorageKey = (locale: Locale) => `qm.uitext.${locale}`;

export interface UiTextSnapshot { version: string; items: Record<string, string> }
type Store = Pick<Storage, 'getItem' | 'setItem'>;
type FetchLike = (input: string, init?: RequestInit) => Promise<Response>;

const memory = new Map<Locale, UiTextSnapshot & { at: number }>();
const inflight = new Map<Locale, Promise<boolean>>();
const applied = new Map<Locale, string>();

/** Accept only a well-formed snapshot: a version string and string values. Anything else is ignored. */
export function parseSnapshot(raw: unknown): UiTextSnapshot | null {
  const o = raw as { version?: unknown; items?: unknown } | null;
  if (!o || typeof o.version !== 'string' || !o.items || typeof o.items !== 'object' || Array.isArray(o.items)) return null;
  const items: Record<string, string> = {};
  for (const [k, v] of Object.entries(o.items)) if (typeof v === 'string') items[k] = v;
  return { version: o.version, items };
}

const defaultStore = (): Store | null => { try { return globalThis.localStorage ?? null; } catch { return null; } };

function apply(locale: Locale, snap: UiTextSnapshot): void {
  if (applied.get(locale) === snap.version) return;
  applied.set(locale, snap.version);
  setTextOverrides(locale, snap.items);
}

/** Apply the copy kept from the last visit, synchronously, so first paint already has the staff wording. */
export function applyCachedUiText(locale: Locale, store: Store | null = defaultStore()): boolean {
  const mem = memory.get(locale);
  if (mem) { apply(locale, mem); return true; }
  try {
    const snap = parseSnapshot(JSON.parse(store?.getItem(uiTextStorageKey(locale)) ?? 'null'));
    if (!snap) return false;
    apply(locale, snap);
    return true;
  } catch { return false; }
}

export interface LoadOptions { fetchFn?: FetchLike; timeoutMs?: number; store?: Store | null; now?: () => number; /** skip the freshness window and revalidate with the server (used by the staff screen right after a publish) */ force?: boolean }

/** Fetch and apply the published overrides for a locale. Resolves true when overrides were applied or already current. */
export function loadUiText(locale: Locale, opts: LoadOptions = {}): Promise<boolean> {
  if (!isLocale(locale)) return Promise.resolve(false);
  const now = opts.now ?? Date.now;
  const mem = memory.get(locale);
  if (!opts.force && mem && now() - mem.at < UI_TEXT_REFRESH_MS) { apply(locale, mem); return Promise.resolve(true); }
  const running = inflight.get(locale);
  if (running) return running;
  const store = opts.store === undefined ? defaultStore() : opts.store;
  applyCachedUiText(locale, store); // warm start; the network answer below replaces it
  const run = (async () => {
    const ctl = typeof AbortController === 'undefined' ? null : new AbortController();
    const timer = setTimeout(() => ctl?.abort(), opts.timeoutMs ?? UI_TEXT_TIMEOUT_MS);
    try {
      const doFetch: FetchLike = opts.fetchFn ?? ((i, init) => fetch(i, init));
      const res = await doFetch(`/bff/v1/cms/public/ui-text?locale=${locale}`, { credentials: 'same-origin', headers: { accept: 'application/json' }, signal: ctl?.signal, ...(opts.force ? { cache: 'no-cache' as const } : {}) });
      if (!res.ok) return false;
      const snap = parseSnapshot(((await res.json()) as { data?: unknown } | null)?.data);
      if (!snap) return false;
      memory.set(locale, { ...snap, at: now() });
      apply(locale, snap);
      try { store?.setItem(uiTextStorageKey(locale), JSON.stringify(snap)); } catch { /* storage blocked or full */ }
      return true;
    } catch {
      return false; // offline, timeout, bad JSON: the shipped text stays
    } finally {
      clearTimeout(timer);
      inflight.delete(locale);
    }
  })();
  inflight.set(locale, run);
  return run;
}

/** Tests: forget everything this module remembers. */
export function resetUiTextCache(): void { memory.clear(); inflight.clear(); applied.clear(); }
